import React from "react";
import { Form } from "react-crud-form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { FormColumns, FormLabel } from "components/form";
import { Input } from "ui/components/input";
import { Switch } from "ui/components/switch";
import FileInput from "components/file-upload/input";
import EmployeeInput from "components/employee/input";
import DepartmentCombobox from "components/directus/department-combobox";
import TitleCombobox from "components/directus/title-combobox";
import GradesCombobox from "components/directus/grades-combobox";
import { PermissionControlledInput } from "permissions/components/input";
import { UserFieldInput } from "permissions/components/user-field-input";
import { useForm } from "react-hook-form";
import { FormActions } from "components/form/actions";
import { useCreateUser } from "@/modules/organization/employees/crud/create/mutation";
import { useUpdateUser } from "@/modules/organization/employees/crud/update/mutation";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { userSchema } from "./schema";
import { getUserById } from "@/modules/organization/employees/crud/form/read";
import { getRequiredFields } from "components/form/utils";
import { useMemo } from "react";
import SelectStatus from "components/select/status";
import SelectRole from "components/directus/role-combobox";
import { DatePicker } from "components/date-picker-2";
import { invalidateAllQueries } from "utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, checkAuthStatus } from "store/src/slices/auth-slice";
import FunctionsCombobox from "components/directus/functions-combobox";

const UserForm = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const form = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      status: "active",
    },
  });

  const requiredFields = useMemo(
    () => getRequiredFields(userSchema),
    [userSchema],
  );

  const { mutateAsync: createUser } = useCreateUser();
  const { mutateAsync: updateUser } = useUpdateUser();

  async function handleCreate(ctx) {
    const values = ctx.values;

    await createUser({ values });
    navigate("/organization/employees");
  }

  async function handleUpdate(id, ctx) {
    try {
      const values = ctx.dirtyValues;
      // Get the user ID from the current form values or initial data
      const currentValues = form.getValues();
      const userId = currentValues.user?.id;
      const response = await updateUser({ id, values, userId });
      invalidateAllQueries();
      if (currentUser && (currentUser.id === id || currentUser.userId === userId)) {
        dispatch(checkAuthStatus());
      }
      form.reset();
      const data = {
        ...response,
        department: response.department?.id,
        grade: response.grade?.id,
        role: response.user?.role?.id,
        status: response.user?.status,
      };
      return data;
    } catch (error) {
      console.log(error);
    }
  }

  async function handleRead(id) {
    const values = await getUserById(id);
    return values;
  }

  return (
    <Form
      form={form}
      create={handleCreate}
      update={handleUpdate}
      read={handleRead}
      itemId={employeeId || "+"}
      schema={userSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        updateItem,
        isUpdating,
        isDirty,
      }) => {
        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="avatar"
              render={({ field }) => (
                <FormItem>
                  <UserFieldInput
                    targetItem={employeeId}
                    field={field.name}
                    collection="employees"
                    type="form"
                  >
                    <FormLabel>Avatar</FormLabel>
                    <FileInput
                      className="size-44"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </UserFieldInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        First Name
                      </FormLabel>
                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Last Name
                      </FormLabel>
                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormColumns>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Email
                      </FormLabel>
                      <Input type="email" {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="employee_id"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Employee ID
                      </FormLabel>
                      <Input type="text" {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="job_title"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Job Title
                    </FormLabel>
                    <TitleCombobox
                      value={field.value}
                      onChange={field.onChange}
                      hideLabel
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Status
                    </FormLabel>
                    <SelectStatus
                      value={field.value}
                      onChange={field.onChange}
                      collection="directus_users"
                      hideLabel
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Title (Display)
                    </FormLabel>
                    <Input {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Role
                    </FormLabel>
                    <SelectRole
                      value={field.value}
                      onChange={field.onChange}
                      hideLabel
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <DepartmentCombobox
                        value={field.value}
                        onChange={field.onChange}
                        required={requiredFields.includes(field.name)}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <GradesCombobox
                        value={field.value}
                        returnItem
                        onChange={(item) => {
                          field.onChange(item?.value);
                          const name = item?.label;

                          if (name) {
                            let factor = null;
                            if (name === "A0") factor = 25;
                            else if (name === "A1") factor = 50;
                            else if (name === "A2") factor = 75;
                            else if (["A3", "A4"].includes(name)) factor = 100;
                            else if (["M1A", "M1B"].includes(name))
                              factor = 125;
                            else if (
                              [
                                "M2A",
                                "M2B",
                                "E1A",
                                "E1B",
                                "E2A",
                                "E2B",
                                "L1",
                                "L2",
                                "L3",
                              ].includes(name)
                            )
                              factor = 150;
                            else factor = 100;

                            if (factor !== null) {
                              form.setValue("consumption_factor", factor, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }
                        }}
                        required={requiredFields.includes(field.name)}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="function"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FunctionsCombobox
                      value={field.value}
                      onChange={field.onChange}
                      required={requiredFields.includes(field.name)}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reports_to"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Reports To
                    </FormLabel>
                    <EmployeeInput
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="allocable"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Allocable
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Enable this to allow the employee to see their allocations
                      in the reports.
                    </p>
                    <Switch
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="secondary_job_title"
              render={({ field }) => (
                <FormItem>
                  <UserFieldInput
                    targetItem={employeeId}
                    field={field.name}
                    collection="employees"
                    type="form"
                  >
                    <FormLabel>Secondary Job Title</FormLabel>
                    <Input {...field} />
                  </UserFieldInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="consumption_factor"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={"create:*"}
                      collection="employees"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Consumption Factor (%)
                      </FormLabel>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        {...field}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val ? Number(val) : "");
                        }}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="joining_date"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={"create:*"}
                    collection="employees"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Joining Date
                    </FormLabel>
                    <DatePicker {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormColumns>
              <FormField
                control={form.control}
                name="phone_no"
                render={({ field }) => (
                  <FormItem>
                    <UserFieldInput
                      targetItem={employeeId}
                      field={field.name}
                      collection="employees"
                      type="form"
                    >
                      <FormLabel>Phone</FormLabel>
                      <Input {...field} />
                    </UserFieldInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emoji"
                render={({ field }) => (
                  <FormItem>
                    <UserFieldInput
                      targetItem={employeeId}
                      field={field.name}
                      collection="employees"
                      type="form"
                    >
                      <FormLabel>Emoji</FormLabel>
                      <Input {...field} />
                    </UserFieldInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>

            <FormField
              control={form.control}
              name="x"
              render={({ field }) => (
                <FormItem>
                  <UserFieldInput
                    targetItem={employeeId}
                    field={field.name}
                    collection="employees"
                    type="form"
                  >
                    <FormLabel>X (Twitter)</FormLabel>
                    <Input {...field} />
                  </UserFieldInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="linkedin"
              render={({ field }) => (
                <FormItem>
                  <UserFieldInput
                    targetItem={employeeId}
                    field={field.name}
                    collection="employees"
                    type="form"
                  >
                    <FormLabel>LinkedIn</FormLabel>
                    <Input {...field} />
                  </UserFieldInput>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <UserFieldInput
                    targetItem={employeeId}
                    field={field.name}
                    collection="employees"
                    type="form"
                  >
                    <FormLabel>Website</FormLabel>
                    <Input {...field} />
                  </UserFieldInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              rule={isNewItem ? "create:*" : "update:*"}
              collection="employees"
              isNewItem={isNewItem}
              isCreating={isCreating}
              createItem={() =>
                createItem().then(() => navigate("/organization/employees"))
              }
              isDirty={isDirty}
              updateItem={updateItem}
              isUpdating={isUpdating}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default UserForm;
